import { useState, useEffect, useCallback } from 'react';
import { settingsApi, adminApi } from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { useSettings } from '../context/SettingsContext';
import { toast } from '../components/Toast';
import Icon from '../components/Icon';
// Shared with the Command Center's inline editor — same control, same row.
import Toggle from '../components/SettingsToggle';

export default function SettingsPage() {
  const { restaurantId } = useAuth();
  const { vatRate: currentVat, refreshSettings } = useSettings();
  const [restaurants,             setRestaurants]             = useState([]);
  const [vatRate,                 setVatRate]                 = useState(currentVat);
  const [paymentTrackingEnabled,  setPaymentTrackingEnabled]  = useState(true);
  const [budgetModuleEnabled,     setBudgetModuleEnabled]     = useState(false);
  const [dishCostingEnabled,      setDishCostingEnabled]      = useState(false);
  const [topProductsEnabled,      setTopProductsEnabled]      = useState(false);
  const [dokkaEmail,              setDokkaEmail]              = useState('');
  const [dokkaForwardTypes,       setDokkaForwardTypes]       = useState([]);
  const [saving,                  setSaving]                  = useState(false);
  // The version of the row these fields were loaded from. Sent back on save so
  // a Command Center edit made in the meantime is reported instead of silently
  // overwritten — this screen and the console's customer card write one row.
  const [loadedAt,                setLoadedAt]                = useState(null);

  // NOTE there is deliberately nothing about יעדי תקציב on this page. Both of
  // its controls have moved to the budget sheet's header on
  // /categories-management, which is where the person who runs the budget
  // already is — and this page is admin-only, so the duplicate could not be
  // reached by the manager who needed it. The VAT basis went first (targets are
  // ex-VAT, so there was nothing to choose); יחידת היעד followed it.
  //
  // `PUT /api/settings/targets` still exists and is still requireWrite — the
  // sheet calls it. This page does not.

  // Must stay in sync with VALID_DOC_TYPES in server/routes/settings.js.
  const DOC_TYPES = [
    'חשבונית', 'חשבונית זיכוי', 'חשבונית מרכזת',
    'תעודת משלוח', 'תעודת החזרה',
    'חשבונית/קבלה', 'קבלה', 'חשבון עסקה', 'כרטסת', 'אחר',
  ];

  const toggleForwardType = (type) => {
    setDokkaForwardTypes(prev =>
      prev.includes(type) ? prev.filter(t => t !== type) : [...prev, type]
    );
  };

  // Put a server copy of the row on screen and remember its version. Both must
  // move together: fields without the matching version would make the next save
  // collide with edits it already agrees with.
  const applyLoaded = useCallback((data) => {
    setVatRate(data.vat_rate ?? 18);
    setPaymentTrackingEnabled(!!data.payment_tracking_enabled);
    setBudgetModuleEnabled(!!data.budget_module_enabled);
    setDishCostingEnabled(!!data.dish_costing_enabled);
    setTopProductsEnabled(!!data.top_products_enabled);
    setDokkaEmail(data.dokka_email || '');
    setDokkaForwardTypes(Array.isArray(data.dokka_forward_types) ? data.dokka_forward_types : []);
    setLoadedAt(data.updated_at ?? null);
  }, []);

  // Load restaurants list (to show which restaurant's settings we're editing)
  useEffect(() => {
    adminApi.getRestaurants().then(r => setRestaurants(r.data)).catch(() => {});
  }, []);

  // Load settings when effective restaurant changes
  useEffect(() => {
    if (!restaurantId) return;
    settingsApi.get().then(r => {
      applyLoaded(r.data);
    }).catch(() => {});
  }, [restaurantId, applyLoaded]);

  const currentRestaurantName = restaurants.find(r => String(r.id) === String(restaurantId))?.name;

  const handleSave = async (e) => {
    e.preventDefault();
    if (!restaurantId) { toast('יש לבחור מסעדה בראש הדף לפני שמירה', 'error'); return; }
    setSaving(true);
    try {
      const res = await settingsApi.update({
        vat_rate: vatRate,
        payment_tracking_enabled: paymentTrackingEnabled,
        budget_module_enabled: budgetModuleEnabled,
        dish_costing_enabled: dishCostingEnabled,
        top_products_enabled: topProductsEnabled,
        dokka_email: dokkaEmail.trim(),
        dokka_forward_types: dokkaForwardTypes,
        expected_updated_at: loadedAt,
      });
      // Re-baseline on the version we just wrote. Without this, saving twice
      // without a reload would collide with your own first save.
      setLoadedAt(res.data.updated_at ?? null);

      // Immediately propagate to all components using SettingsContext
      refreshSettings();
      toast('ההגדרות נשמרו בהצלחה');
    } catch (err) {
      toast(err.response?.data?.error || 'שגיאה בשמירה', 'error');
      // A conflict means someone saved from the Command Center while this form
      // was open. Retrying sends the same stale version and fails identically,
      // so put their version on screen instead and let the change be re-applied
      // on top of it.
      if (err.response?.status === 409 && err.response.data?.current) {
        applyLoaded(err.response.data.current);
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <h1 className="page-title">הגדרות</h1>

      {!restaurantId ? (
        <div className="card" style={{ textAlign: 'center', padding: '40px 24px' }}>
          <Icon name="store" size={30} style={{ margin: '0 auto 12px', color: 'var(--ink-faint)' }} />
          <div style={{ fontWeight: 600, color: 'var(--ink-muted)', marginBottom: 8 }}>יש לבחור מסעדה</div>
          <div style={{ fontSize: '0.88rem', color: 'var(--ink-muted)' }}>
            בחר מסעדה מהרשימה בסרגל הניווט כדי לערוך את ההגדרות שלה
          </div>
        </div>
      ) : (
        <form onSubmit={handleSave}>
          <div className="card">
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 24 }}>
              <div style={{ background: 'var(--selected-soft-bg)', borderRadius: 8, padding: '6px 14px', fontWeight: 700, color: 'var(--selected-soft-ink)', fontSize: '0.92rem' }}>
                <Icon name="store" size={15} /> {currentRestaurantName || `מסעדה ${restaurantId}`}
              </div>
              <span style={{ fontSize: '0.82rem', color: 'var(--ink-subtle)' }}>הגדרות יחולו על מסעדה זו בלבד</span>
            </div>

            {/* Payment tracking */}
            <div style={{ marginBottom: 28 }}>
              <h3 style={{ fontSize: '0.88rem', fontWeight: 700, color: 'var(--ink-muted)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 16 }}>
                תשלומים
              </h3>
              <Toggle
                checked={paymentTrackingEnabled}
                onChange={setPaymentTrackingEnabled}
                label="תיעוד תשלומים"
                description="הצגת אפשרויות תשלום, היסטוריית תשלומים וסינון לפי סטטוס תשלום"
              />
            </div>

            <div style={{ borderTop: '1px solid var(--hairline)', marginBottom: 24 }} />

            {/* מודול הוצאות והכנסות — gates the two budget widgets on /analysis,
                the /budget page, and every /api/revenue route. Default off; the
                same toggle sits on the Command Center customer card. */}
            <div style={{ marginBottom: 28 }}>
              <h3 style={{ fontSize: '0.88rem', fontWeight: 700, color: 'var(--ink-muted)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 16 }}>
                הוצאות והכנסות
              </h3>
              <Toggle
                checked={budgetModuleEnabled}
                onChange={setBudgetModuleEnabled}
                label="מודול הוצאות והכנסות"
                description="קטגוריות, יעדי תקציב והזנת הכנסות — כולל שני הווידג'טים בעמוד הניתוח ועמוד הניהול"
              />
            </div>

            <div style={{ borderTop: '1px solid var(--hairline)', marginBottom: 24 }} />

            {/* עץ מוצר — gates the /dishes screens and every /api/dishes route.
                Default off; a viewer never sees the module even when it is on. */}
            <div style={{ marginBottom: 28 }}>
              <h3 style={{ fontSize: '0.88rem', fontWeight: 700, color: 'var(--ink-muted)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 16 }}>
                עץ מוצר
              </h3>
              <Toggle
                checked={dishCostingEnabled}
                onChange={setDishCostingEnabled}
                label="מודול עץ מוצר"
                description="בניית מנות מהקטלוג, חישוב עלות מנה והתראות על שינויי מחיר. נראה למנהלים ולמנהלי מערכת בלבד — לא לצופים"
              />
            </div>

            <div style={{ borderTop: '1px solid var(--hairline)', marginBottom: 24 }} />

            {/* המוצרים המובילים ברכש — gates the /analysis card, its report and
                every /api/analysis/top-products route. Default off; every role
                sees it once on. Not on the Command Center card. */}
            <div style={{ marginBottom: 28 }}>
              <h3 style={{ fontSize: '0.88rem', fontWeight: 700, color: 'var(--ink-muted)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 16 }}>
                ניתוח רכש
              </h3>
              <Toggle
                checked={topProductsEnabled}
                onChange={setTopProductsEnabled}
                label="המוצרים המובילים ברכש"
                description="ווידג'ט ודוח בעמוד הניתוח: המוצרים שעליהם הוצא הכי הרבה כסף בתקופה, עם מחיר מינימום, מקסימום ונוכחי ושינוי המחיר — כדי לזהות על מה כדאי לנהל משא ומתן. נראה לכל המשתמשים במסעדה"
              />
            </div>

            <div style={{ borderTop: '1px solid var(--hairline)', marginBottom: 24 }} />

            {/* VAT rate */}
            <div>
              <h3 style={{ fontSize: '0.88rem', fontWeight: 700, color: 'var(--ink-muted)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 16 }}>
                חשבונאות
              </h3>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16 }}>
                <div>
                  <div style={{ fontWeight: 600, fontSize: '0.95rem', color: 'var(--ink)' }}>שיעור מע"מ</div>
                  <div style={{ fontSize: '0.82rem', color: 'var(--ink-muted)', marginTop: 3 }}>
                    ישמש בחישוב מע"מ אוטומטי בעת עריכת מסמך
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    step="0.1"
                    value={vatRate}
                    onChange={e => setVatRate(parseFloat(e.target.value) || 0)}
                    style={{
                      width: 80, minHeight: 'var(--tap-min)', padding: '6px 10px', border: '1px solid var(--field-edge)',
                      borderRadius: 8, fontSize: '1rem', fontWeight: 600, textAlign: 'center',
                      direction: 'ltr',
                    }}
                  />
                  <span style={{ fontWeight: 600, color: 'var(--ink-muted)' }}>%</span>
                </div>
              </div>
            </div>

            <div style={{ borderTop: '1px solid var(--hairline)', margin: '24px 0' }} />

            {/* Dokka — accounting forwarding */}
            <div>
              <h3 style={{ fontSize: '0.88rem', fontWeight: 700, color: 'var(--ink-muted)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 16 }}>
                העברה להנהלת חשבונות (Dokka)
              </h3>

              <div style={{ marginBottom: 20 }}>
                <div style={{ fontWeight: 600, fontSize: '0.95rem', color: 'var(--ink)', marginBottom: 6 }}>
                  כתובת מייל ב-Dokka
                </div>
                <div style={{ fontSize: '0.82rem', color: 'var(--ink-muted)', marginBottom: 10 }}>
                  כתובת המייל הייחודית של המסעדה במערכת Dokka — אליה יישלחו המסמכים המאושרים.
                </div>
                <input
                  type="email"
                  value={dokkaEmail}
                  onChange={e => setDokkaEmail(e.target.value)}
                  placeholder="restaurant-name@dokka.com"
                  style={{
                    width: '100%', maxWidth: 380, minHeight: 'var(--tap-min)', padding: '8px 12px',
                    border: '1px solid var(--field-edge)', borderRadius: 8,
                    fontSize: '0.95rem', direction: 'ltr',
                  }}
                />
              </div>

              <div>
                <div style={{ fontWeight: 600, fontSize: '0.95rem', color: 'var(--ink)', marginBottom: 6 }}>
                  סוגי מסמכים שיועברו אוטומטית בעת אישור
                </div>
                <div style={{ fontSize: '0.82rem', color: 'var(--ink-muted)', marginBottom: 12 }}>
                  מסמך מסוג שמסומן כאן יישלח אוטומטית ל-Dokka ברגע שיאושר. שאר הסוגים אפשר לשלוח ידנית מתוך עמוד המסמך.
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 8 }}>
                  {DOC_TYPES.map(t => (
                    <label
                      key={t}
                      className={`check-tile${dokkaForwardTypes.includes(t) ? ' is-on' : ''}`}
                    >
                      <input
                        type="checkbox"
                        checked={dokkaForwardTypes.includes(t)}
                        onChange={() => toggleForwardType(t)}
                      />
                      <span>{t}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div style={{ display: 'flex', gap: 12 }}>
            <button type="submit" className="btn btn-success" disabled={saving}>
              {saving ? 'שומר...' : <><Icon name="save" size={16} /> שמור הגדרות</>}
            </button>
          </div>
        </form>
      )}

      {/* The duplicate-document-numbers panel moved to the Command Center (now
          the last card on its מבט על screen). It never belonged here: this screen is scoped to the
          restaurant you have selected, while the duplicates report is
          cross-tenant — so its document links landed you in whatever restaurant
          happened to be active rather than the one the duplicate is in. */}
    </div>
  );
}
